import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDoc, getDocs, doc, setDoc, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';

const RECENT_DAYS = 30;

const PrayerRecommendation = ({ discipleId, mentorId }) => {
  const [loading, setLoading] = useState(true);
  const [prayer, setPrayer] = useState(null);
  const [error, setError] = useState('');
  const [marking, setMarking] = useState(false);
  const [marked, setMarked] = useState(false);

  useEffect(() => {
    if (!discipleId || !mentorId) return;
    let isMounted = true;
    const fetchPrayer = async () => {
      setLoading(true);
      setError('');
      try {
        // 1. Get current missional scale
        const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', mentorId);
        const mentorDetailsSnap = await getDoc(mentorDetailsRef);
        const missionalScale = mentorDetailsSnap.exists() ? mentorDetailsSnap.data().missionalScale : null;
        if (missionalScale === null || missionalScale === undefined) throw new Error('No missional scale set.');

        // 2. Get recently used prayer IDs (last 30 days)
        let recentPrayerIds = [];
        try {
          const historyCol = collection(db, 'disciples', discipleId, 'mentorDetails', mentorId, 'prayerHistory');
          const since = new Date();
          since.setDate(since.getDate() - RECENT_DAYS);
          const historySnap = await getDocs(historyCol);
          recentPrayerIds = historySnap.docs
            .filter(doc => {
              const d = doc.data().dateRecommended?.toDate?.() || doc.data().dateRecommended;
              return d && new Date(d) > since;
            })
            .map(doc => doc.id);
        } catch (err) {
          // Silently handle permission errors for prayerHistory - continue without recent prayer filtering
          const errorCode = err?.code;
          if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
            console.warn('Error fetching prayer history:', err);
          }
        }

        // 3. Get all eligible prayers
        const prayersCol = collection(db, 'prayers');
        let eligible = [];
        try {
          const q = query(prayersCol, where('missionalScale', '==', missionalScale));
          const prayersSnap = await getDocs(q);
          eligible = prayersSnap.docs.filter(doc => !recentPrayerIds.includes(doc.id));

          // If none, try anyLevel prayers
          if (eligible.length === 0) {
            const anyLevelQ = query(prayersCol, where('anyLevel', '==', true));
            const anyLevelSnap = await getDocs(anyLevelQ);
            eligible = anyLevelSnap.docs.filter(doc => !recentPrayerIds.includes(doc.id));
          }

          // If still none, allow repeat (but prefer not to)
          if (eligible.length === 0) {
            eligible = prayersSnap.docs.length > 0 ? prayersSnap.docs : [];
          }
        } catch (err) {
          // Silently handle permission errors for prayers collection
          const errorCode = err?.code;
          if (errorCode === 'permission-denied' || errorCode === 'firestore/permission-denied') {
            // Don't show error, just don't display prayer
            throw new Error('Unable to load prayers.');
          }
          throw err;
        }
        
        if (eligible.length === 0) {
          throw new Error('No prayers found for this level.');
        }

        // 4. Pick a random eligible prayer
        const chosen = eligible[Math.floor(Math.random() * eligible.length)];
        const prayerData = { id: chosen.id, ...chosen.data() };
        if (isMounted) setPrayer(prayerData);

        // 5. Log the recommendation in prayerHistory (fail silently if permission denied)
        try {
          const historyCol = collection(db, 'disciples', discipleId, 'mentorDetails', mentorId, 'prayerHistory');
          await setDoc(doc(historyCol, chosen.id), {
            prayerId: chosen.id,
            dateRecommended: new Date(),
          }, { merge: true });
        } catch (historyErr) {
          // Silently handle permission errors when logging prayer history
          const errorCode = historyErr?.code;
          if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
            console.warn('Error logging prayer history:', historyErr);
          }
        }
      } catch (err) {
        // Check if it's a permission error
        const errorCode = err?.code;
        if (errorCode === 'permission-denied' || errorCode === 'firestore/permission-denied') {
          // Don't show error message for permission errors - just don't display prayer
          if (isMounted) setError('');
        } else {
          if (isMounted) setError(err.message || 'Failed to fetch prayer.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchPrayer().catch((err) => {
      // Extra safety: catch any unhandled rejections
      const errorCode = err?.code;
      if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
        if (isMounted) {
          setError(err.message || 'Failed to fetch prayer.');
        }
      }
      if (isMounted) {
        setLoading(false);
      }
    });
    return () => { isMounted = false; };
  }, [discipleId, mentorId]);

  // Helper to mark prayer as prayed
  const handleMarkPrayed = async () => {
    if (!prayer || !discipleId || !mentorId) return;
    setMarking(true);
    try {
      // Write to mentorDetails/prayers subcollection
      const prayerRef = doc(db, 'disciples', discipleId, 'mentorDetails', mentorId, 'prayers', prayer.id);
      await setDoc(prayerRef, {
        prayerId: prayer.id,
        prayedAt: Timestamp.now(),
        prayerText: prayer.prayerText || '',
      }, { merge: true });
      setMarked(true);
    } catch (err) {
      // Check if it's a permission error
      const errorCode = err?.code;
      if (errorCode === 'permission-denied' || errorCode === 'firestore/permission-denied') {
        // Silently handle permission errors - still show as marked locally
        setMarked(true);
      } else {
        setError('Could not mark as prayed.');
      }
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="mt-6 max-w-xl mx-auto bg-gray-50 border border-gray-200 rounded-xl shadow-sm px-6 py-4 text-center">
      <div className="text-xs text-gray-400 mb-1 font-semibold uppercase tracking-wide">Suggested Prayer for Today</div>
      {loading ? (
        <div className="text-gray-400 italic">Loading prayer...</div>
      ) : error ? (
        <div className="text-red-400 italic">{error}</div>
      ) : prayer ? (
        <>
          {/* <div className="font-semibold text-gray-700 mb-1">{prayer.prayerDescriptor || '(No Descriptor)'}</div> */}
          <div className="text-gray-500 italic text-sm whitespace-pre-line">{prayer.prayerText}</div>
          {/* Prayed button - modern UI */}
          <div className="mt-3 flex justify-center">
            <button
              onClick={handleMarkPrayed}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition
                ${marked ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400 hover:bg-green-50 hover:text-green-600'}
              `}
              disabled={marking || marked}
              aria-pressed={marked}
            >
              {marked ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2l4 -4" /></svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2l4 -4" /></svg>
              )}
              {marked ? 'Prayed' : 'Pray'}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default PrayerRecommendation; 